import { NAME_KEY } from "@/lib/printess/constants";
import Table from "@/lib/printess/table";
import SourceMenu from "@/lib/printess/source_menu/sourceMenu";

class MappingOrchestrator {
  #ctx;
  #sourceMenu;
  #sourceMappings;

  // Source mappings are single source to target.
  // They are given as multiple sources to target, but we simplify here.
  // Only source mappings with a target table that we can find, and source items that we can find, are built.
  static async buildSourceMappings(ctx, sourceMenu) {
    const rawMappings = await ctx.metadata("source_mappings");
    if (!rawMappings || rawMappings.length === 0) return [];

    const results = [];

    for (const mapping of rawMappings) {
      const targetTable = await Table.find(ctx.editor.api, mapping.target);
      if (!targetTable) continue;

      const mappingsForTarget = mapping.source.reduce((acc, sourceName) => {
        const menuItems = sourceMenu.whereCategory(sourceName);

        if (menuItems.length === 0) return acc;

        return [
          ...acc,
          new SourceMapping(
            sourceName,
            menuItems,
            targetTable,
            mapping.options || {},
          ),
        ];
      }, []);

      results.push(...mappingsForTarget);
    }

    return results;
  }

  constructor(ctx) {
    this.#ctx = ctx;
    this.#sourceMenu = SourceMenu.fromRaw(this.#ctx.sourceMenuDataValue);
  }

  async perform() {
    this.#sourceMappings = await MappingOrchestrator.buildSourceMappings(
      this.#ctx,
      this.#sourceMenu,
    );

    this.#sourceMappings.forEach((sourceMapping) => {
      sourceMapping.perform();
    });
  }
}

// Simple menu item wrapper to keep track of which items have been added already
// during a mapping.
class SourceMappingCandidate {
  constructor(menuItem) {
    this.menuItem = menuItem;
    this.isAdded = false;
  }

  added() {
    this.isAdded = true;
  }
}

class SourceMapping {
  #sourceName;
  #menuItems;
  #targetTable;
  #options;

  constructor(sourceName, menuItems, targetTable, options) {
    this.#sourceName = sourceName;
    this.#menuItems = menuItems;
    this.#targetTable = targetTable;
    this.#options = options;
  }

  get hasAddItems() {
    return this.#options.add_items !== false;
  }

  get hasOverwriteExistingItems() {
    return this.#options.overwrite_existing_items !== false;
  }

  perform() {
    console.group(
      `Performing mapping ${this.#sourceName} -> ${this.#targetTable.name}`,
    );

    const candidates = this.#menuItems.map(
      (menuItem) => new SourceMappingCandidate(menuItem),
    );

    // The first time we look through the menu for matches only.
    console.group("Adding matching candidates.");
    this.addMatchingCandidates(candidates);
    console.groupEnd();

    if (this.hasAddItems) {
      console.group("Adding remaining items.");
      this.addCandidates(candidates.filter((candidate) => !candidate.isAdded));
      console.groupEnd();
    }

    this.#targetTable.write();
    console.groupEnd();
  }

  addMatchingCandidates(candidates) {
    candidates.forEach((candidate) => {
      const menuItem = candidate.menuItem;
      const matchingProductRow = this.#targetTable.matchingProductRow(
        menuItem.name,
      );

      if (!matchingProductRow) {
        console.debug(`❌ "${menuItem.name}": no match.`);
        return;
      }

      console.debug(
        `🔄 "${menuItem.name}": matched "${matchingProductRow.name}". Overwriting.`,
      );
      // We want to preserve the name on the target, since it's likely the canonical
      // form. The matched form may not be correct.
      matchingProductRow.updateFromMenuItem(menuItem, { preserve: [NAME_KEY] });
      return candidate.added();
    });
  }

  addCandidates(candidates) {
    candidates.forEach((candidate) => {
      const menuItem = candidate.menuItem;
      // first we try to overwrite an existing row that hasn't been locked copyAsTemplate
      // We don't do this if we have overwrite_existing_items false.
      if (this.hasOverwriteExistingItems) {
        const row = this.#targetTable.unlockedProducts[0];
        if (row) {
          console.debug(
            `🔄 "${menuItem.name}": found "${row.name}". Overwriting`,
          );
          row.updateFromMenuItem(menuItem);
          return candidate.added();
        }
      }

      if (!this.hasAddItems) return;

      // Add a new row for it
      console.debug(`🆕 "${menuItem.name}": no rows left. Adding.`);
      this.#targetTable.addFromMenuItem(menuItem);
      return candidate.added();
    });
  }
}

export const MagicMenuAutomapMixin = (Base) =>
  class extends Base {
    async pushSourceMenu() {
      console.debug("Automapping source menu.");
      const orchestrator = new MappingOrchestrator(this);
      await orchestrator.perform();
    }
  };
